// src/public/rsvp/parts/SessionAccordion.jsx
import React, { useMemo } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Icon from '@mui/material/Icon';
import { alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { safeText, fmtDateTR, fmtTimeTR } from '../utils/rsvpFormatters';
import { compactAccordionSx, compactTitleSx, compactSubSx } from './rsvpCompactSx';

function firstNameOnly(v) {
  const s = (v ?? '').toString().trim();
  if (!s) return '—';
  return s.split(/\s+/)[0];
}

function InfoRow({ icon, label, value, last = false }) {
  return (
    <MDBox
      sx={(t) => ({
        display: 'grid',
        gridTemplateColumns: '24px 78px 1fr',
        alignItems: 'center',
        gap: 1,
        py: 0.7,
        px: 0.75,
        borderRadius: 6,
        background:
          t.palette.mode === 'dark'
            ? alpha(t.palette.common.white, 0.035)
            : alpha(t.palette.common.black, 0.02),
        border: `1px solid ${
          t.palette.mode === 'dark'
            ? alpha(t.palette.common.white, 0.08)
            : alpha(t.palette.common.black, 0.06)
        }`,
      })}
    >
      <Icon sx={{ fontSize: 18, opacity: 0.9 }}>{icon}</Icon>

      <MDTypography
        variant="caption"
        sx={{
          opacity: 0.72,
          fontWeight: 900,
          letterSpacing: 0.2,
          fontSize: 11,
          textTransform: 'uppercase',
        }}
        noWrap
      >
        {label}
      </MDTypography>

      <MDTypography
        variant="body2"
        sx={{ fontWeight: 900, lineHeight: 1.15, fontSize: 13, minWidth: 0 }}
        noWrap
      >
        {value}
      </MDTypography>
    </MDBox>
  );
}

export function SessionAccordion({
  memberName,
  trainerName,
  serviceName,
  studioName,
  startAt,
  endAt,
}) {
  const title = safeText(serviceName, 'Pilates');
  const dateLabel = `${fmtDateTR(startAt)} • ${fmtTimeTR(startAt)}`;
  const timeLabel = `${fmtTimeTR(startAt)}${endAt ? ` – ${fmtTimeTR(endAt)}` : ''}`;

  const memberLabel = useMemo(() => firstNameOnly(memberName), [memberName]);
  const placeLabel = studioName ? safeText(studioName) : '—';

  return (
    <Accordion defaultExpanded sx={compactAccordionSx}>
      <AccordionSummary expandIcon={<Icon sx={{ fontSize: 18 }}>expand_more</Icon>}>
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography sx={compactTitleSx}>{title}</MDTypography>
          <MDTypography sx={compactSubSx}>{dateLabel}</MDTypography>
        </MDBox>
      </AccordionSummary>

      <AccordionDetails>
        <MDBox display="grid" gap={0.5}>
          <InfoRow icon="person" label="Üye" value={memberLabel} />
          <InfoRow icon="fitness_center" label="Eğitmen" value={safeText(trainerName)} />
          <InfoRow icon="place" label="Stüdyo" value={placeLabel} />
          <InfoRow icon="schedule" label="Saat" value={timeLabel} />
        </MDBox>
      </AccordionDetails>
    </Accordion>
  );
}
